// 018A — Transanestésico (Fase 1B, ver PLAN.md). Reemplaza el stub anterior
// (un selector de `Procedure` + signos vitales sueltos, ~5% del spec real).
//
// **Tanda 12 de 3**: Bloque A (encabezado MSP) + Bloque D (registro
// transanestésico — tabla horaria + gráfico de signos vitales), la pieza de
// mayor riesgo técnico y la única que 019 reusa tal cual.
// **Tanda 13 de 3**: Bloque F (técnica anestésica, `tecnicaAnestesica.ts`/
// `TecnicaAnestesica.tsx`).
// **Tanda 14 de 3 (cierra 018A)**: Bloques B/C/E/G-S
// (`bloquesRestantes018ADatos.ts`/`BloquesRestantes018A.tsx`) — únicos
// recursos FHIR reales de todo 018A: hasta 2 `Condition` (diagnóstico
// preoperatorio/postoperatorio del bloque B), mismo patrón que
// `Formulario038.tsx` (una sola transacción: se guardan las dos o ninguna).
//
// Los 7 parámetros continuos del gráfico (TAS/TAD/TAM/FC/temperatura/PVC/
// SatO2) NO se persisten como `Observation` — decisión de diseño explícita
// en PLAN.md: sin consumidor real hoy (una cirugía es un evento acotado, no
// hay vista de "signos vitales intraoperatorios" en el expediente). Viven
// solo en el estado del formulario y en `diagramaSignosVitales` (dibujado
// en el PDF firmado vía `dibujarSignosVitales`, `@logiacare/hce-pdf`).
import type { JSX } from 'react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { type DatosSignosVitalesPdf } from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { datosSignosVitalesPdf, GraficoSignosVitalesAnestesia, type EventoAnestesia, type MedicionPuntual } from './signosVitalesAnestesia';
import { SiNoBotones } from './camposSimples';
import { textoSiNo, type SiNoValor } from './camposSimplesDatos';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { TecnicaAnestesica } from './TecnicaAnestesica';
import { estadoInicialTecnicaAnestesica, paresDeTecnicaAnestesia } from './tecnicaAnestesicaDatos';
import { codeDeDiagnostico, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { Bloque018ABC, Bloque018AE, Bloque018AGaS } from './BloquesRestantes018A';
import { estadoInicialBloquesRestantes018A, paresDeBloquesRestantes018A } from './bloquesRestantes018ADatos';
import { columnaHorariaVacia, estadoInicialTablaHoraria, FILAS_AGENTE, paresDeTablaHoraria, PARAMETROS_MONITOREO, TOPE_COLUMNAS_HORARIAS, type ColumnaHoraria, type ParametroMonitoreo } from './tablaHorariaTransanestesicaDatos';

const CODIGO = '018A';

const RESPIRACION_OPCIONES = ['espontanea', 'asistida', 'controlada'] as const;

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };

export function Formulario018A({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f018a.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Registro transanestésico: el papel no trae servicio; es de quirófano.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Quirófano' });

  const [tabla, setTabla] = useState(() => estadoInicialTablaHoraria());
  const columnas = tabla.columnas;
  const [respiracion, setRespiracion] = useState('');
  const [consentimiento, setConsentimiento] = useState<SiNoValor>('');
  const [torniquete, setTorniquete] = useState(false);
  const [feto, setFeto] = useState(false);
  const [posicion, setPosicion] = useState('');
  const [mediciones, setMediciones] = useState<readonly MedicionPuntual[]>([]);
  const [eventos, setEventos] = useState<readonly EventoAnestesia[]>([]);
  const [tecnica, setTecnica] = useState(() => estadoInicialTecnicaAnestesica());
  const [bloques, setBloques] = useState(() => estadoInicialBloquesRestantes018A());
  const [diagnosticoPreoperatorio, setDiagnosticoPreoperatorio] = useState<DiagnosticoCie10Valor>(DIAGNOSTICO_VACIO);
  const [diagnosticoPostoperatorio, setDiagnosticoPostoperatorio] = useState<DiagnosticoCie10Valor>(DIAGNOSTICO_VACIO);

  function actualizarColumna(id: string, cambio: (c: ColumnaHoraria) => ColumnaHoraria): void {
    setTabla((prev) => ({ ...prev, columnas: prev.columnas.map((c) => (c.id === id ? cambio(c) : c)) }));
  }
  function actualizarAgente(id: string, fila: number, valor: string): void {
    actualizarColumna(id, (c) => ({ ...c, agentes: c.agentes.map((v, i) => (i === fila ? valor : v)) }));
  }
  function actualizarMonitoreo(id: string, parametro: ParametroMonitoreo, valor: string): void {
    actualizarColumna(id, (c) => ({ ...c, monitoreo: { ...c.monitoreo, [parametro]: valor } }));
  }

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [
        { pregunta: t('formularios.f018a.consentimientoInformado'), respuesta: textoSiNo(consentimiento, { si: t('formulario.si'), no: t('formulario.no') }) },
        ...paresDeTablaHoraria(t, tabla),
        { pregunta: t('formularios.f018a.bloqueD.respiracion'), respuesta: respiracion ? t(`formularios.f018a.bloqueD.respiracionOpciones.${respiracion}`) : '' },
        { pregunta: t('formularios.f018a.bloqueD.torniquete'), respuesta: torniquete ? t('formulario.si') : '' },
        { pregunta: t('formularios.f018a.bloqueD.feto'), respuesta: feto ? t('formulario.si') : '' },
        { pregunta: t('formularios.f018a.bloqueD.posicion'), respuesta: posicion.trim() },
      ].filter((p) => p.respuesta.trim() !== '');

      const lote = new LoteTransaccional();
      pares.push(...paresDeTecnicaAnestesia(t, tecnica));
      pares.push(...paresDeBloquesRestantes018A(t, bloques));

      if (diagnosticoPreoperatorio.texto.trim()) {
        lote.crear({ resourceType: 'Condition', subject, code: codeDeDiagnostico(diagnosticoPreoperatorio), recordedDate: new Date().toISOString() });
        pares.push({ pregunta: t('formularios.f018a.bloqueB.diagnosticoPreoperatorio'), respuesta: conCodigoCie(diagnosticoPreoperatorio) });
      }
      if (diagnosticoPostoperatorio.texto.trim()) {
        lote.crear({ resourceType: 'Condition', subject, code: codeDeDiagnostico(diagnosticoPostoperatorio), recordedDate: new Date().toISOString() });
        pares.push({ pregunta: t('formularios.f018a.bloqueB.diagnosticoPostoperatorio'), respuesta: conCodigoCie(diagnosticoPostoperatorio) });
      }

      await lote.enviar(medplum);

      const diagramaSignosVitales: DatosSignosVitalesPdf = datosSignosVitalesPdf(mediciones, eventos);

      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, diagramaSignosVitales);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <SiNoBotones id="f018a-consentimiento" etiqueta={t('formularios.f018a.consentimientoInformado')} valor={consentimiento} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={setConsentimiento} />

      <Bloque018ABC
        estado={bloques}
        onChange={setBloques}
        diagnosticoPreoperatorio={diagnosticoPreoperatorio}
        onChangeDiagnosticoPreoperatorio={setDiagnosticoPreoperatorio}
        diagnosticoPostoperatorio={diagnosticoPostoperatorio}
        onChangeDiagnosticoPostoperatorio={setDiagnosticoPostoperatorio}
      />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueD.titulo')}</h4>

      <div className="flex flex-col gap-2">
        <Label>{t('formularios.f018a.bloqueD.tablaHoraria')}</Label>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 min-w-[180px] bg-hce-surface p-2 text-left text-xs font-medium text-hce-muted">{t('formularios.f018a.bloqueD.hora')}</th>
                {columnas.map((c) => (
                  <th key={c.id} className="min-w-[110px] p-2">
                    <div className="flex items-center gap-1">
                      <Input value={c.hora} placeholder="hh:mm" onChange={(e) => actualizarColumna(c.id, (col) => ({ ...col, hora: e.target.value }))} />
                      <button type="button" aria-label={t('formularios.f018a.bloqueD.quitarColumna')} onClick={() => setTabla((prev) => ({ ...prev, columnas: prev.columnas.filter((col) => col.id !== c.id) }))}>
                        <Trash2 className="size-4 text-hce-muted" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: FILAS_AGENTE }, (_, fila) => (
                <tr key={`agente-${fila}`} className="border-t border-border">
                  <td className="sticky left-0 z-10 bg-hce-surface p-2">
                    <CampoDictado
                      id={`f018a-agente-${fila}`}
                      etiqueta={`${t('formularios.f018a.bloqueD.parametros.agenteInhalatorio')} ${fila + 1}`}
                      placeholder={`${t('formularios.f018a.bloqueD.parametros.agenteInhalatorio')} ${fila + 1}`}
                      valor={tabla.nombresAgentes[fila] ?? ''}
                      onChange={(v) => setTabla((prev) => ({ ...prev, nombresAgentes: prev.nombresAgentes.map((n, i) => (i === fila ? v : n)) }))}
                    />
                  </td>
                  {columnas.map((c) => (
                    <td key={c.id} className="p-2">
                      <Input aria-label={`${t('formularios.f018a.bloqueD.parametros.agenteInhalatorio')} ${fila + 1}`} value={c.agentes[fila] ?? ''} onChange={(e) => actualizarAgente(c.id, fila, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
              {PARAMETROS_MONITOREO.map((parametro) => (
                <tr key={parametro} className="border-t border-border">
                  <td className="sticky left-0 z-10 bg-hce-surface p-2 text-xs text-hce-ink">{t(`formularios.f018a.bloqueD.parametros.${parametro}`)}</td>
                  {columnas.map((c) => (
                    <td key={c.id} className="p-2">
                      <Input aria-label={t(`formularios.f018a.bloqueD.parametros.${parametro}`)} value={c.monitoreo[parametro]} onChange={(e) => actualizarMonitoreo(c.id, parametro, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          disabled={columnas.length >= TOPE_COLUMNAS_HORARIAS}
          onClick={() => setTabla((prev) => ({ ...prev, columnas: [...prev.columnas, columnaHorariaVacia()] }))}
        >
          {t('formularios.f018a.bloqueD.agregarColumna')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f018a.bloqueD.respiracion')}</Label>
          <Select value={respiracion || undefined} onValueChange={setRespiracion}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESPIRACION_OPCIONES.map((op) => (
                <SelectItem key={op} value={op}>
                  {t(`formularios.f018a.bloqueD.respiracionOpciones.${op}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 self-end pb-2">
          <Checkbox id="f018a-torniquete" checked={torniquete} onCheckedChange={(v) => setTorniquete(v === true)} />
          <Label htmlFor="f018a-torniquete" className="font-normal">
            {t('formularios.f018a.bloqueD.torniquete')}
          </Label>
        </div>
        <div className="flex items-center gap-2 self-end pb-2">
          <Checkbox id="f018a-feto" checked={feto} onCheckedChange={(v) => setFeto(v === true)} />
          <Label htmlFor="f018a-feto" className="font-normal">
            {t('formularios.f018a.bloqueD.feto')}
          </Label>
        </div>
      </div>

      <CampoDictado id="f018a-posicion" etiqueta={t('formularios.f018a.bloqueD.posicion')} valor={posicion} onChange={setPosicion} />

      <div className="flex flex-col gap-2">
        <Label>{t('formularios.f018a.bloqueD.grafico')}</Label>
        <GraficoSignosVitalesAnestesia mediciones={mediciones} onChangeMediciones={setMediciones} eventos={eventos} onChangeEventos={setEventos} />
      </div>

      <Bloque018AE estado={bloques} onChange={setBloques} />

      <TecnicaAnestesica estado={tecnica} onChange={setTecnica} />

      <Bloque018AGaS estado={bloques} onChange={setBloques} />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
